import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Site } from '../lib/db';
import { useCurrentSite } from '../lib/currentSite';
import { SiteSwitcherSheet } from './SiteSwitcherSheet';

interface Props {
  site: Site | null;
}

/**
 * The active-site banner.
 *
 * This is the single most safety-critical surface in the app — it tells the
 * user which site every form, sign-in, RAMS, accident report and photo will
 * be filed against. The Brutal Review flagged the previous one-liner as a
 * top cause of "misfile data within the first week and blame us" complaints,
 * so this version is deliberately:
 *
 *   • Large — full card, large name, hard to skim past.
 *   • Persistent — already rendered on every page that creates records.
 *   • Switchable in two taps — "Switch site" → pick from a list.
 *   • Safety-checked — an explicit confirmation modal calls out the
 *     consequence of switching in plain English (handled inside the
 *     SiteSwitcherSheet via useConfirm).
 *
 * The component keeps the same {site} prop shape as before so callers
 * (Dashboard / Registers / SiteRegister) don't change.
 */
export function ActiveSiteBanner({ site }: Props) {
  const { sites, setCurrentSite, currentSiteId } = useCurrentSite();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  if (!site) {
    return (
      <>
        <div className="active-site-card empty" role="alert">
          <div className="active-site-card-pill">
            <span className="active-site-card-pill-dot" aria-hidden="true">!</span>
            No active site
          </div>
          <div className="active-site-card-name">Choose a site first</div>
          <div className="active-site-card-sub">
            Records you create need a site. Anything you fill in now could go to
            the wrong place — pick one before you start.
          </div>
          <div className="active-site-card-actions">
            {sites.length > 0 ? (
              <button
                type="button"
                className="active-site-card-cta"
                onClick={() => setSwitcherOpen(true)}
              >
                Choose a site
              </button>
            ) : (
              <Link to="/sites?add=1" className="active-site-card-cta">
                Add your first site
              </Link>
            )}
          </div>
        </div>
        <SiteSwitcherSheet
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          sites={sites}
          currentSiteId={currentSiteId}
          onPick={setCurrentSite}
        />
      </>
    );
  }

  const subtitle = projectTypeLabel(site.project_type) || site.address || null;
  const otherSiteCount = Math.max(0, sites.length - 1);

  return (
    <>
      <div className="active-site-card" data-site-id={site.id}>
        <div className="active-site-card-pill">
          <span className="active-site-card-pill-dot" aria-hidden="true">●</span>
          Active site
        </div>
        <div className="active-site-card-name">{site.name}</div>
        {subtitle && <div className="active-site-card-sub">{subtitle}</div>}
        <div className="active-site-card-actions">
          <button
            type="button"
            className="active-site-card-cta"
            onClick={() => setSwitcherOpen(true)}
          >
            Switch site
            {otherSiteCount > 0 && (
              <span className="active-site-card-cta-count" aria-hidden="true">
                · {otherSiteCount} other{otherSiteCount === 1 ? '' : 's'}
              </span>
            )}
          </button>
          {/* Direct "+ Add site" affordance so users don't have to open the
              switcher and hunt for the add action. One tap from the home. */}
          <Link
            to="/sites?add=1"
            className="active-site-card-cta active-site-card-cta-add"
          >
            + Add site
          </Link>
        </div>
      </div>
      <Link to={`/sites/${site.id}/setup`} className="site-setup-pill">
        ✓ Site setup checklist →
      </Link>
      <SiteSwitcherSheet
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        sites={sites}
        currentSiteId={currentSiteId}
        onPick={setCurrentSite}
      />
    </>
  );
}

function projectTypeLabel(t: Site['project_type']): string | null {
  if (t === 'quick_job') return 'Quick job';
  if (t === 'small_refurb') return 'Small refurb';
  if (t === 'full_site') return 'Full site';
  return null;
}
